import React from 'react'
import FooterContactViewConsultantTeam from './FooterContactViewConsultantTeam.jsx'
import ContactBanner from './ContactBanner.jsx'
import { ErrorBoundary } from 'react-error-boundary'
import { isProduction } from '../../utility/config/environments.js'
import { isInvestorAdvisorOrInstitutional } from '../../../global/authentication/authentication-manager.js'
import { buildBundleUrl, useFetch } from './services.js'

const Phones = ({ el, site }) => {
  return (
    el.label &&
    el.value && (
      <div key={el.label + el.value} className="List-item phones">
        <dt className="List-itemLabel">
          {el.label}
          {el.subtext && ` (${el.subtext})`}
        </dt>
        <dd className="List-itemValue u-textRight">
          <a
            className={`Link ${
              site === 'institutional' ? 'u-textColorDefault' : ''
            }`}
            href={`tel:${el.value}`}
          >
            {el.value}
          </a>
        </dd>
      </div>
    )
  )
}

const Emails = ({ el }) => {
  return (
    el.label &&
    el.url && (
      <div key={el.label + el.value} className="List-item">
        <dt className="List-itemLabel">{el.label}</dt>
        <dd className="List-itemValue u-textRights u-flex u-flexAlignItemsCenter u-flexJustifyEnd">
          <span className="Icon--material u-textSizeMsrp1 u-marginRight1gu u-alignBottom">
            email
          </span>
          <a className="Link" href={el.url} aria-label="Send a message">
            Send a message
          </a>
        </dd>
      </div>
    )
  )
}

const ContactDefinition = ({ el, site }) => {
  return el.type === 'email' ? (
    <Emails el={el} />
  ) : (
    <Phones el={el} site={site} />
  )
}

const HeadingSubtext = ({ heading, subtext, classes }) => {
  const styles = [
    'Footer-contactHeading',
    'u-textColorBrandDarker',
    'u-paddingRight2gu',
    classes,
  ]
    .join(' ')
    .trim()
  return (
    <>
      {heading && <h3 className={styles}>{heading}</h3>}
      {subtext && (
        <p className="Footer-subtext u-paddingRight2gu u-textSizeMsrm1">
          {subtext}
        </p>
      )}
    </>
  )
}

const ContactRows = ({ contactInfo, rowClasses = '', site }) => {
  return (
    <dl
      className={`List List--definition u-marginTop4gu u-paddingRight4gu Footer-ListBorder u-sm-paddingRight0 u-textSecondary ${rowClasses}`}
    >
      {contactInfo.map((el) => {
        return (
          <ContactDefinition
            key={`${el.type}-${el.label}`}
            el={el}
            site={site}
          />
        )
      })}
    </dl>
  )
}

const ContactSection = ({
  heading,
  subtext,
  contactInfo,
  classes,
  rowClasses,
}) => {
  return (
    <>
      <HeadingSubtext heading={heading} subtext={subtext} classes={classes} />
      <ContactRows contactInfo={contactInfo} rowClasses={rowClasses} />
    </>
  )
}

const bundleKeys = {
  advisor: {
    bundleName: 'cti-advisor.contact-us-mutual-funds',
    contactTopHeadingKey: 'requests-footer-heading',
    subtextKey: 'requests-subtext',
    contactBottomHeadingKey: 'requests-footer-heading-email',
    contactBottomSubtextKey: 'requests-subtext-email',
    numberKeys: ['financial', 'broker', 'registered', 'independent', 'dealer'],
    emailKeys: ['services', 'products', 'sales'],
    shareHolderKeys: ['shareholder', 'transaction'],
    contactUsKeys: {
      icon: 'button-login-icon',
      loginUrl: 'button-login.url',
      loginLabel: 'button-login',
      contactUrl: 'button-option-contact.url',
      contactLabel: 'button-option-contact.label',
      buttonOptionsText: 'button-options.text',
    },
  },
  investor: {
    bundleName: 'cti-investor.contact-us-mutual-funds',
    contactTopHeadingKey: 'requests-heading',
    subtextKey: 'requests-subtext',
    contactBottomHeadingKey: 'shareholder-heading',
    contactBottomSubtextKey: 'shareholder-subtext',
    numberKeys: ['shareholder', 'transaction'],
    emailKeys: ['services', 'products'],
    shareHolderKeys: ['shareholderCollege'],
    contactUsKeys: {
      icon: 'button-login-icon',
      loginUrl: 'button-help.url',
      loginLabel: 'button-help.label',
      contactUrl: 'button-options.url',
      contactLabel: 'button-options.label',
      buttonOptionsText: 'button-options.text',
    },
  },
  institutional: {
    bundleName: 'cti-institutional.contact-us',
    contactTopHeadingKey: 'request-heading-footer',
    subtextKey: 'requests-subtext',
    contactBottomHeadingKey: '',
    contactBottomSubtextKey: '',
    numberKeys: ['specialist'],
    emailKeys: ['products'],
    shareHolderKeys: [],
    contactUsKeys: {
      icon: 'button-login-icon',
      loginUrl: 'button-login.url',
      loginLabel: 'button-login',
      contactUrl: 'button-options.url',
      contactLabel: 'button-options.label',
      buttonOptionsText: 'button-options.text',
    },
  },
}
const FooterDefaultContent = () => {
  const site = isInvestorAdvisorOrInstitutional()
  const {
    bundleName,
    numberKeys,
    emailKeys,
    shareHolderKeys,
    contactTopHeadingKey,
    contactBottomHeadingKey,
    subtextKey,
    contactBottomSubtextKey,
    contactUsKeys,
  } = bundleKeys[site]

  const url = buildBundleUrl(bundleName)
  const { data, isError, isLoading } = useFetch(url)
  if (isError || isLoading) return <div></div>

  const bundle = data.bundles.default
  const title = bundle['title']
  const viewConsultantTeam = {
    icon: bundle[contactUsKeys.icon],
    loginUrl: bundle[contactUsKeys.loginUrl],
    loginLabel: bundle[contactUsKeys.loginLabel],
    contactUrl: bundle[contactUsKeys.contactUrl],
    contactLabel: bundle[contactUsKeys.contactLabel],
    buttonOptionsText: bundle[contactUsKeys.buttonOptionsText],
  }

  const getNumbers = (key) => {
    return {
      type: 'phone',
      key,
      value: bundle[`number-${key}.value`],
      label: bundle[`number-${key}.label`],
      subtext: bundle[`number-${key}.subtext`],
    }
  }
  const getEmails = (key) => {
    return {
      type: 'email',
      key,
      url: bundle[`email-${key}.url`],
      label: bundle[`email-${key}.label`],
    }
  }

  const numbers = numberKeys.map(getNumbers)
  const shareholderNumbers = shareHolderKeys.map(getNumbers)
  const emails = emailKeys.map(getEmails)

  const contactSectionTop = {
    heading: bundle[contactTopHeadingKey],
    subtext: bundle[subtextKey],
    contactInfo:
      site === 'investor'
        ? [...numbers, emails.find((e) => e.key === 'services')]
        : numbers,
  }
  const contactSectionBottom = {
    heading: bundle[contactBottomHeadingKey],
    subtext: bundle[contactBottomSubtextKey],
    contactInfo: {
      investor: [
        ...shareholderNumbers,
        emails.find((e) => e.key === 'products'),
      ],
      advisor: emails,
      institutional: emails,
    }[site],
  }

  const shareholderSectionBottom = {
    heading: bundle['shareholder-heading'],
    subtext: bundle['shareholder-subtext'],
    contactInfo: shareholderNumbers,
  }

  const ContactTop = ({ site, contactSectionTop }) => {
    return site === 'institutional' ? (
      <>
        <HeadingSubtext
          heading={contactSectionTop.heading}
          subtext={contactSectionTop.subtext}
        />
        <div className="Footer-contactTop u-md-size6of12 u-lg-size6of12">
          <ContactRows
            contactInfo={contactSectionTop.contactInfo}
            site={site}
          />
        </div>
      </>
    ) : (
      <div className="Footer-contactTop u-md-size6of12 u-lg-size6of12">
        <ContactSection
          heading={contactSectionTop.heading}
          subtext={contactSectionTop.subtext}
          contactInfo={contactSectionTop.contactInfo}
        />
      </div>
    )
  }

  return (
    <section
      className="Footer-contactSection"
      id="contact-us"
      data-qa-automation="contact-us-footer"
    >
      {title && <ContactBanner title={title} site={site} />}
      <footer-wholesaler></footer-wholesaler>

      <div
        id="footer-default-content"
        className="Grid u-sizeConstrained u-padding0 u-marginVert8gu"
      >
        {contactSectionTop ? (
          <ContactTop site={site} contactSectionTop={contactSectionTop} />
        ) : null}
        {contactSectionBottom && (
          <div className="Footer-contactBottom u-md-size6of12 u-lg-size6of12">
            <ContactSection
              classes={'u-sm-paddingTop6gu'}
              heading={contactSectionBottom.heading}
              subtext={contactSectionBottom.subtext}
              contactInfo={contactSectionBottom.contactInfo}
              rowClasses={site === 'institutional' ? 'u-sm-marginTop0' : ''}
            />
            {shareholderSectionBottom && site === 'advisor' ? (
              <ContactSection
                classes={'u-marginTop6gu u-marginBottom1gu'}
                heading={shareholderSectionBottom.heading}
                subtext={shareholderSectionBottom.subtext}
                contactInfo={shareholderSectionBottom.contactInfo}
              />
            ) : null}
          </div>
        )}
        {viewConsultantTeam && (
          <FooterContactViewConsultantTeam
            icon={viewConsultantTeam.icon}
            loginUrl={viewConsultantTeam.loginUrl}
            loginLabel={viewConsultantTeam.loginLabel}
            buttonOptionsText={viewConsultantTeam.buttonOptionsText}
            contactUrl={viewConsultantTeam.contactUrl}
            contactLabel={viewConsultantTeam.contactLabel}
          />
        )}
      </div>
    </section>
  )
}

export default function ErrorWrapperFooterDefaultContent(props) {
  return (
    <ErrorBoundary
      fallbackRender={() =>
        !isProduction() ? <p>Error in FooterDefaultContent component</p> : null
      }
    >
      <FooterDefaultContent {...props}></FooterDefaultContent>
    </ErrorBoundary>
  )
}
